import React, { useState, useEffect, useRef, ReactNode, TouchEvent, BaseSyntheticEvent, useMemo } from 'react';
import { Box, Drawer, IconButton, CloseIcon, useTheme, Container } from '@methanesat/ui-components';
import { SnapPoints, SnapPointIds } from '../../types';
import { useDispatch, useSelector } from 'react-redux';
import { selectIsAnyChipExpanded, toggleAllChips } from '../../reducers';
import type { RootState } from '../../store';
import { MOBILE_DRAWER_PREVIEW_HEIGHT } from '../../consts';

export const getClosestSnapPoint = (snapPoints: SnapPoints, height: number) => {
    const keys = Object.keys(snapPoints);
    // Compare each snap point to the current drawer height and return the one with the smallest difference
    const closestPoint = keys.reduce((prev, curr) => {
        return Math.abs(snapPoints[curr as SnapPointIds] - height) < Math.abs(snapPoints[prev as SnapPointIds] - height)
            ? curr
            : prev;
    });

    return { name: closestPoint, value: snapPoints[closestPoint as SnapPointIds] };
};

export interface BottomUpDrawerProps {
    header?: ReactNode;
    open: boolean | undefined;
    onClose: (event: BaseSyntheticEvent, reason: 'backdropClick' | 'escapeKeyDown') => void;
    drawerContent: ReactNode;
    snapPoint: SnapPointIds;
    setSnapPoint: (s: SnapPointIds) => void;
    resetScrollOnChange?: string;
}

export const BottomUpDrawer = ({
    header,
    open,
    onClose,
    drawerContent,
    snapPoint,
    setSnapPoint,
    resetScrollOnChange
}: BottomUpDrawerProps) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();
    const dispatch = useDispatch();

    const [currentHeight, setCurrentHeight] = useState<number>(MOBILE_DRAWER_PREVIEW_HEIGHT);
    const [windowHeight, setWindowHeight] = useState<number>(0);
    const isAnyChipExpanded = useSelector((state: RootState) => selectIsAnyChipExpanded(state));

    //scroll the drawer content back to the top
    useEffect(() => {
        if (contentRef && 'current' in contentRef && contentRef.current) {
            contentRef.current.scrollTop = 0;
        }
    }, [resetScrollOnChange, contentRef]);

    //ensure we have innerHeight when we try to use it
    useEffect(() => {
        function handleResize() {
            if (typeof window !== 'undefined') {
                setWindowHeight(window.innerHeight);
            }
        }
        handleResize();

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    //calculate the various views the drawer can snap to
    const createSnapPoints = (windowHeight: number) => {
        return {
            [SnapPointIds.preview]: MOBILE_DRAWER_PREVIEW_HEIGHT,
            [SnapPointIds.half]: windowHeight * 0.5,
            [SnapPointIds.full]: windowHeight * 0.85
        };
    };

    const SNAP_POINTS = useMemo(() => {
        return createSnapPoints(windowHeight);
    }, [windowHeight]);

    //for dragging calculations and animation
    const startY = useRef<number | null>(null);
    const startHeight = useRef<number>(0);
    const lastY = useRef<number>(0);
    const draggedHeight = useRef(currentHeight); //real time height during drag
    const animationFrame = useRef<number | null>(null);

    // Cleanup frame on unmount
    useEffect(() => {
        return () => {
            if (animationFrame.current) {
                cancelAnimationFrame(animationFrame.current);
            }
        };
    }, []);

    //if the snap point is changed from somewhere else due to another interaction, update the height
    useEffect(() => {
        if (snapPoint !== null) {
            setCurrentHeight(SNAP_POINTS[SnapPointIds[snapPoint as keyof typeof SnapPointIds]]);
        }
    }, [snapPoint, SNAP_POINTS]);

    //if any mini legend is open and the drawer is not closed or in preview already, snap to preview
    useEffect(() => {
        if (
            isAnyChipExpanded !== null &&
            isAnyChipExpanded &&
            snapPoint !== SnapPointIds.preview &&
            snapPoint !== null
        ) {
            setCurrentHeight(SNAP_POINTS[SnapPointIds.preview]);
            setSnapPoint(SnapPointIds.preview);
        }
    }, [isAnyChipExpanded, SNAP_POINTS, setSnapPoint, snapPoint]);

    //Shared handlers for updating height of card
    const startDrag = (y: number) => {
        startY.current = y;
        startHeight.current = currentHeight;
    };

    const updateDrag = (y: number) => {
        if (startY.current === null) return;
        lastY.current = y;
        if (animationFrame.current === null) {
            animationFrame.current = requestAnimationFrame(() => {
                const deltaY = startY.current !== null ? lastY.current - startY.current : 0;
                const newHeight = Math.max(
                    SNAP_POINTS.preview,
                    Math.min(SNAP_POINTS.full, startHeight.current - deltaY)
                );
                draggedHeight.current = newHeight;
                setCurrentHeight(newHeight);
                animationFrame.current = null;
            });
        }
    };

    const endDrag = (height: number) => {
        const snapped = getClosestSnapPoint(SNAP_POINTS, height);
        setCurrentHeight(snapped.value);
        draggedHeight.current = snapped.value;
        setSnapPoint(SnapPointIds[snapped.name as SnapPointIds]);
        startY.current = null;
        if (isAnyChipExpanded && snapped.name !== SnapPointIds.preview) {
            dispatch(toggleAllChips(false));
        }
    };

    //Touch handlers
    const handleTouchStart = (e: TouchEvent) => {
        startDrag(e.touches[0].clientY);
    };

    const handleTouchMove = (e: TouchEvent) => {
        updateDrag(e.touches[0].clientY);
    };

    const handleTouchEnd = () => {
        endDrag(currentHeight);
    };

    //Mouse handlers
    const handleMouseDown = (e: MouseEvent) => {
        e.preventDefault();
        startDrag(e.clientY);

        const onMouseMove = (e: MouseEvent) => {
            updateDrag(e.clientY);
        };

        const onMouseUp = () => {
            endDrag(draggedHeight.current);

            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };

        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
    };

    const closeClickHandler = (e: BaseSyntheticEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onClose(e, 'backdropClick');
    };

    return (
        <Drawer
            anchor="bottom"
            open={open}
            onClose={onClose}
            hideBackdrop={true}
            variant="persistent"
            ModalProps={{
                keepMounted: true
            }}
            slotProps={{
                paper: {
                    sx: (theme) => ({
                        height: currentHeight,
                        overflow: 'visible',
                        transition: 'height 0.25s ease',
                        borderTopLeftRadius: 28,
                        borderTopRightRadius: 28,
                        marginLeft: 'auto',
                        marginRight: 'auto',
                        [theme.breakpoints.down('md')]: {
                            maxWidth: '675px'
                        },
                        [theme.breakpoints.down('sm')]: {
                            maxWidth: '600px'
                        }
                    })
                }
            }}
        >
            {/* Pull Bar Section*/}
            <Box
                data-testid="bottom-up-drawer-pull-bar"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onMouseDown={(e: React.MouseEvent) => handleMouseDown(e.nativeEvent)}
                component="div"
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'grab',
                    touchAction: 'none'
                }}
            >
                {/* Grey Pull Bar*/}
                <Box
                    sx={{
                        width: 80,
                        [theme.breakpoints.down('sm')]: {
                            width: 40
                        },
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: theme.palette.grey[600],
                        margin: 1
                    }}
                />
                {/* Title and Close button */}
                <Container
                    sx={{
                        display: 'flex',
                        width: 'stretch',
                        zIndex: 1
                    }}
                >
                    {header ? (
                        header
                    ) : (
                        <Box>
                            <IconButton
                                data-testid="bottom-up-drawer-close"
                                onClick={(e: BaseSyntheticEvent) => {
                                    closeClickHandler(e);
                                }}
                                sx={{ color: theme.palette.grey[400] }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    )}
                </Container>
            </Box>

            {/* Drawer Content */}
            <Box
                sx={{ height: '100%', backgroundColor: theme.palette.secondary.main, overflow: 'auto' }}
                ref={contentRef}
            >
                {drawerContent}
            </Box>
        </Drawer>
    );
};
